import { useCallback, useEffect, useState } from 'react';

// material-ui
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import IconButton from 'components/@extended/IconButton';
import { notificacoesService, rotaDaNotificacao, MinhaNotificacaoDto } from 'modules/notificacoes/services/notificacoesService';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import FolderOpenOutlined from '@ant-design/icons/FolderOpenOutlined';

const POLL_MS = 45000;

// ==============================|| HEADER CONTENT - NOTIFICATION (in-app / sino) ||============================== //

export default function NotificationMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [itens, setItens] = useState<MinhaNotificacaoDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const carregarContagem = useCallback(async () => {
    const n = await notificacoesService.minhas.naoLidas().catch(() => null);
    if (typeof n === 'number') setUnread(n);
  }, []);

  // Poll do contador de não-lidas (badge). SignalR fica para evolução.
  useEffect(() => {
    carregarContagem();
    const t = setInterval(carregarContagem, POLL_MS);
    return () => clearInterval(t);
  }, [carregarContagem]);

  const carregarLista = useCallback(async () => {
    setLoading(true);
    try {
      const pagina = await notificacoesService.minhas.listar({ tamanhoPagina: 10 });
      setItens(pagina?.itens ?? []);
      setTotal(pagina?.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggle = () => {
    const novo = !open;
    setOpen(novo);
    if (novo) carregarLista();
  };

  const handleMarkAllRead = async () => {
    await notificacoesService.minhas.marcarTodasLidas().catch(() => {});
    const agora = new Date().toISOString();
    setItens((prev) => prev.map((n) => ({ ...n, lidaEm: n.lidaEm ?? agora })));
    setUnread(0);
  };

  const handleClick = async (n: MinhaNotificacaoDto) => {
    if (!n.lidaEm) {
      await notificacoesService.minhas.marcarLida(n.id).catch(() => {});
      setUnread((u) => Math.max(0, u - 1));
      setItens((prev) => prev.map((x) => (x.id === n.id ? { ...x, lidaEm: new Date().toISOString() } : x)));
    }
    setOpen(false);
    const rota = rotaDaNotificacao(n.linkUrl);
    if (rota) navigate(rota);
  };

  const handleArquivar = async (n: MinhaNotificacaoDto) => {
    await notificacoesService.minhas.arquivar(n.id).catch(() => {});
    setItens((prev) => prev.filter((x) => x.id !== n.id));
    setTotal((t) => Math.max(0, t - 1));
    if (!n.lidaEm) setUnread((u) => Math.max(0, u - 1));
  };

  const verTodas = () => {
    setOpen(false);
    navigate('/minhas-notificacoes');
  };

  const quando = (iso: string) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
    } catch {
      return '';
    }
  };

  return (
    <>
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip title="Notificações">
          <IconButton
            color="secondary"
            variant="light"
            sx={{ color: 'text.primary', bgcolor: open ? 'grey.100' : 'transparent' }}
            aria-label="open notifications"
            onClick={handleToggle}
          >
            <Badge badgeContent={unread} color="primary">
              <BellOutlined />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      <Drawer
        sx={{ zIndex: 2001 }}
        anchor="right"
        onClose={handleToggle}
        open={open}
        slotProps={{ paper: { sx: { width: { xs: 340, sm: 420 } } } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, pb: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>Notificações</Typography>
          {unread > 0 && (
            <Button size="small" startIcon={<CheckOutlined />} onClick={handleMarkAllRead}>
              Marcar todas lidas
            </Button>
          )}
        </Stack>
        <Divider />
        {loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Carregando…</Typography>
        ) : itens.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Sem notificações.</Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {itens.map((n) => (
              // Botão dentro de botão é HTML inválido — por isso a ação de arquivar fica FORA do ListItemButton.
              <Box
                key={n.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: n.lidaEm ? 'transparent' : 'action.hover'
                }}
              >
                <ListItemButton onClick={() => handleClick(n)} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: n.lidaEm ? 'transparent' : 'primary.main', mt: 0.9, mr: 1, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: n.lidaEm ? 400 : 600 }}>{n.assunto}</Typography>
                    {n.corpoTexto && (
                      <Typography variant="body2" color="text.secondary"
                        sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {n.corpoTexto}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">{quando(n.criadaEm)}</Typography>
                  </Box>
                </ListItemButton>
                <Tooltip title="Arquivar">
                  <IconButton color="secondary" size="small" sx={{ mt: 1.25, mr: 1 }} onClick={() => handleArquivar(n)}>
                    <FolderOpenOutlined />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </List>
        )}
        <Divider />
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Button size="small" onClick={verTodas}>
            {total > itens.length ? `Ver todas (${total})` : 'Ver todas as notificações'}
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
